import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit, inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ReactiveSearchHomeComponent } from '@nucleus/ui-base';
import { Audit } from '@nucleus/audit-report-ui';
import { QuerySortDirection } from '@nucleus/ui-base';
import { AuditDetailViewComponent } from "@nucleus/audit-report-ui";
import { AuditService } from '../../services/audit/audit.service';
import { AuditTrailFilter } from '../../services/audit/audit-trail-filter';
import { FilterRequest } from '../../services/filter-request';
import { Page } from '@nucleus/ui-base';
import { ServiceHandler } from '@nucleus/ui-base';
import { PageHeaderComponent } from "@nucleus/ui-base";
import { PageResult } from '@nucleus/ui-base';

export interface TraceData {
  entityName: string;
  entityId: string;
}

/** Per-row audit trail dialog. The entity scope (entityName + entityId) is
 *  fixed by the row that opened the dialog and never changes — no filter
 *  controls, no reset. Heavy-export features (XLSX / CSV / PDF / print)
 *  are intentionally NOT here: the audit-report page already exposes them
 *  for filtered queries, and the per-row dialog stays a focused viewer. */
@Component({
  selector: 'app-audit-trace',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatDialogModule,
    MatIconModule,
    MatPaginatorModule,
    MatSortModule,
    MatTableModule,
    MatTooltipModule,
    PageHeaderComponent,
  ],
  templateUrl: './audit-trace.component.html',
  styleUrl: './audit-trace.component.css',
})
export class AuditTraceComponent extends ReactiveSearchHomeComponent<Audit, AuditTrailFilter> implements OnInit {

  displayedColumns = ['create-timestamp', 'username', 'action', 'description', 'star'];

  private detailDialog = inject(MatDialog);

  constructor(
    private auditService: AuditService,
    public dialogRef: MatDialogRef<AuditTraceComponent>,
    @Inject(MAT_DIALOG_DATA) public data: TraceData,
  ) {
    super(auditService as any, 'createTimestamp', QuerySortDirection.DESC);
  }

  ngOnInit(): void {
    // entity scope is fixed by the row that opened the dialog — never changes
    this.filter.update(() => new AuditTrailFilter(this.data.entityName, this.data.entityId));
    this.refreshData(true);
  }

  override createFilter(): AuditTrailFilter {
    return new AuditTrailFilter();
  }

  override onResetFilter(): void {
    // no-op: the dialog has no filter controls; entity scope is row-fixed
  }

  /** Override the paginated search to call AuditService.filterTrail()
   *  rather than the default .filter() — same paging contract, different
   *  endpoint (resourceAccessService.canUserAccessResource enforced). */
  override onSearch(filter: AuditTrailFilter, page: Page, handler: ServiceHandler<PageResult<Audit>>): void {
    const request = new FilterRequest<AuditTrailFilter>(filter);
    request.page = page;
    this.auditService.filterTrail(handler, request);
  }

  onViewDetails(audit: Audit): void {
    this.detailDialog.open(AuditDetailViewComponent, {
      panelClass: 'bw-dialog-panel',
      width: '700px',
      maxWidth: '90vw',
      height: '72vh',
      data: { auditRecord: audit },
    });
  }

  closeDialog(): void {
    this.dialogRef.close(true);
  }

  /** Color-coded badge for the action column — green for create, blue for
   *  read, amber for update, red for delete. Falls back to neutral for
   *  unrecognised verbs. */
  getActionClass(action: string): string {
    const a = (action || '').toLowerCase();
    if (a.includes('create') || a.includes('add') || a.includes('upload')) return 'action-create';
    if (a.includes('update') || a.includes('edit') || a.includes('modif')) return 'action-update';
    if (a.includes('delete') || a.includes('purge') || a.includes('remove')) return 'action-delete';
    if (a.includes('view') || a.includes('read') || a.includes('get')) return 'action-read';
    return '';
  }
}
